import { FormFieldType } from '@uikit/components/FormField/consts';
import FormField from '@uikit/components/FormField/FormField';
import type { FormFieldElement } from '@uikit/components/FormField/interfaces';
import FormFieldDropdown from '@uikit/components/FormFieldDropdown/FormFieldDropdown';
import type { FormFieldDropdownOption } from '@uikit/components/FormFieldDropdown/interfaces';
import type { IconVariants } from '@uikit/components/Icon/interfaces';
import { useState, type ChangeEvent } from 'react';

export type ConsumptionOption = {
  readonly label?: string;
  readonly value: string;
};

type StageBoxConsumptionSelectProps = {
  readonly name: string;
  readonly label: string;
  readonly options: ConsumptionOption[];
  readonly value?: string;
  readonly onChange?: (value: string) => void;
  readonly isBoxOnGradient?: boolean;
  readonly customOptionLabel?: string;
  readonly className?: string;
  readonly prefixBoxIcon?: IconVariants;
};

const CUSTOM_VALUE = '__custom__';

const StageBoxConsumptionSelect = ({
  name,
  label,
  options,
  value = '',
  onChange,
  isBoxOnGradient = false,
  customOptionLabel = 'Verbrauch selbst eingeben',
  className,
  prefixBoxIcon,
}: StageBoxConsumptionSelectProps) => {
  const [isCustom, setIsCustom] = useState(false);

  const matchesPreset = options.some((option) => option.value === value);
  const showCustomInput = isCustom || (value !== '' && !matchesPreset);

  const dropdownOptions: FormFieldDropdownOption[] = [
    ...options.map((option) => ({
      label: option.label ?? option.value,
      value: option.value,
    })),
    { label: customOptionLabel, value: CUSTOM_VALUE },
  ];

  const handleDropdownChange = (option: FormFieldDropdownOption | null) => {
    if (!option) {
      return;
    }

    if (option.value === CUSTOM_VALUE) {
      setIsCustom(true);
      onChange?.('');
      return;
    }

    setIsCustom(false);
    onChange?.(option.value);
  };

  const handleInputChange = (event: ChangeEvent<FormFieldElement>) => {
    onChange?.(event.target.value.replace(/\D/g, ''));
  };

  const handleReset = () => {
    setIsCustom(false);
    onChange?.('');
  };

  if (showCustomInput) {
    return (
      <FormField
        className={className}
        name={name}
        label={label}
        value={value}
        type={FormFieldType.Text}
        inputMode="numeric"
        restrictMaxLength
        suffix="kWh / Jahr"
        prefixBoxIcon={prefixBoxIcon}
        icon="action/close"
        onClickIcon={handleReset}
        onChange={handleInputChange}
        isBoxOnGradient={isBoxOnGradient}
        errorMessageHasRelativePosition
        autoFocus={isCustom}
      />
    );
  }

  return (
    <FormFieldDropdown
      className={className}
      name={name}
      label={label}
      ariaLabel={label}
      options={dropdownOptions}
      value={matchesPreset ? value : ''}
      onChange={handleDropdownChange}
      isBoxOnGradient={isBoxOnGradient}
      errorMessageHasRelativePosition
      portalList
      prefixBoxIcon={prefixBoxIcon}
    />
  );
};

export default StageBoxConsumptionSelect;
